// Copyright 2026 Bitech.id. Licensed under the MIT License.

/**
 * Session-event capture for Biwave Stream — LogRocket-style discrete events
 * (console, network via fetch/XHR, static resource loads, click, navigation)
 * that the backend can count, filter, and search per session, complementing
 * the opaque rrweb chunk stream. Uploaded batched to
 * POST /api/stream/sessions/:id/events (see StreamTransport.sendEvents).
 * Metadata only — request/response bodies and headers are never captured.
 */

const MAX_CONSOLE_MESSAGE_CHARS = 4096;
const MAX_URL_CHARS = 2048;
const MAX_SELECTOR_CHARS = 1024;
const MAX_CLICK_TEXT_CHARS = 256;

export type ConsoleLevel = 'debug' | 'log' | 'info' | 'warn' | 'error';

export type SessionEvent =
  | { ts: number; type: 'console'; level: ConsoleLevel; message: string }
  | {
      ts: number;
      type: 'network';
      method: string;
      url: string;
      status: number;
      duration_ms: number;
    }
  | {
      ts: number;
      type: 'resource';
      initiator_type: string;
      url: string;
      duration_ms: number;
      size: number;
    }
  | { ts: number; type: 'click'; selector: string; text: string }
  | { ts: number; type: 'navigation'; from: string; to: string };

export interface SessionEventCaptureOptions {
  /** The SDK's own ingest origin — its requests are never captured as network events. */
  ignoreOrigin: string;
  /** Mirrors the recorder's maskAllText — when true, click text is omitted unless the element opts out via 'biwave-unmask'. */
  maskAllText: boolean;
  onEvent: (event: SessionEvent) => void;
}

/** Installs all instrumentations; returns an uninstall that restores every patched global. */
export function installSessionEventCapture(options: SessionEventCaptureOptions): () => void {
  const uninstalls = [
    installConsoleCapture(options),
    installNetworkCapture(options),
    installXHRCapture(options),
    installResourceCapture(options),
    installClickCapture(options),
    installNavigationCapture(options),
  ];
  return () => {
    for (const uninstall of uninstalls) uninstall();
  };
}

type OriginalConsoleFn = (...args: unknown[]) => void;

function installConsoleCapture(options: SessionEventCaptureOptions): () => void {
  const methods: ConsoleLevel[] = ['debug', 'log', 'info', 'warn', 'error'];
  const originals = new Map<ConsoleLevel, OriginalConsoleFn>();

  for (const method of methods) {
    // Kept unbound (and .apply'd) so uninstall restores the exact original.
    const original = (console as unknown as Record<string, OriginalConsoleFn>)[method];
    if (typeof original !== 'function') continue;
    originals.set(method, original);

    (console as unknown as Record<string, OriginalConsoleFn>)[method] = (...args: unknown[]) => {
      // Same exclusion as console-breadcrumbs: the SDK's own internal logging
      // is noise about the SDK, not app behavior.
      const isOwnLog = typeof args[0] === 'string' && args[0].startsWith('[Biwave');
      if (!isOwnLog) {
        options.onEvent({
          ts: Date.now(),
          type: 'console',
          level: method,
          message: args
            .map((a) => stringifyArg(a))
            .join(' ')
            .slice(0, MAX_CONSOLE_MESSAGE_CHARS),
        });
      }
      original.apply(console, args);
    };
  }

  return () => {
    for (const [method, original] of originals) {
      (console as unknown as Record<string, OriginalConsoleFn>)[method] = original;
    }
  };
}

function installNetworkCapture(options: SessionEventCaptureOptions): () => void {
  if (typeof window.fetch !== 'function') return () => {};

  const original = window.fetch.bind(window);

  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    const url = resolveUrl(input);
    if (url.startsWith(options.ignoreOrigin)) {
      return original(input, init);
    }

    const method = resolveMethod(input, init);
    const startTime = Date.now();

    const emit = (status: number): void => {
      options.onEvent({
        ts: startTime,
        type: 'network',
        method,
        url: url.slice(0, MAX_URL_CHARS),
        status,
        duration_ms: Date.now() - startTime,
      });
    };

    return original(input, init).then(
      (response) => {
        emit(response.status);
        return response;
      },
      (error: unknown) => {
        emit(0); // status 0 = network failure, per the ingest contract
        throw error;
      },
    );
  }) as typeof fetch;

  return () => {
    window.fetch = original;
  };
}

interface XHRMeta {
  method: string;
  url: string;
  startTime: number;
}

type XHRWithBiwaveMeta = XMLHttpRequest & { __biwave?: XHRMeta };

function installXHRCapture(options: SessionEventCaptureOptions): () => void {
  if (typeof XMLHttpRequest !== 'function') return () => {};

  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function patchedOpen(
    this: XHRWithBiwaveMeta,
    method: string,
    url: string | URL,
    ...rest: unknown[]
  ) {
    const urlString = typeof url === 'string' ? url : url.toString();
    if (!urlString.startsWith(options.ignoreOrigin)) {
      this.__biwave = { method, url: urlString, startTime: 0 };
    }
    return (originalOpen as (...args: unknown[]) => void).apply(this, [method, url, ...rest]);
  } as typeof XMLHttpRequest.prototype.open;

  XMLHttpRequest.prototype.send = function patchedSend(
    this: XHRWithBiwaveMeta,
    ...args: unknown[]
  ) {
    const meta = this.__biwave;
    if (meta) {
      meta.startTime = Date.now();
      this.addEventListener('loadend', () => {
        options.onEvent({
          ts: meta.startTime,
          type: 'network',
          method: meta.method,
          url: meta.url.slice(0, MAX_URL_CHARS),
          status: this.status,
          duration_ms: Date.now() - meta.startTime,
        });
      });
    }
    return (originalSend as (...args: unknown[]) => void).apply(this, args);
  } as typeof XMLHttpRequest.prototype.send;

  return () => {
    XMLHttpRequest.prototype.open = originalOpen;
    XMLHttpRequest.prototype.send = originalSend;
  };
}

// Already captured explicitly (with HTTP status) by installNetworkCapture/installXHRCapture —
// the resource-timing entries for these initiator types would otherwise double-report them.
const RESOURCE_CAPTURE_IGNORED_INITIATOR_TYPES = new Set(['fetch', 'xmlhttprequest']);

function installResourceCapture(options: SessionEventCaptureOptions): () => void {
  if (typeof PerformanceObserver !== 'function') return () => {};

  const emitEntry = (entry: PerformanceResourceTiming): void => {
    if (RESOURCE_CAPTURE_IGNORED_INITIATOR_TYPES.has(entry.initiatorType)) return;
    if (entry.name.startsWith(options.ignoreOrigin)) return;

    options.onEvent({
      ts: Math.round(performance.timeOrigin + entry.startTime),
      type: 'resource',
      initiator_type: entry.initiatorType || 'other',
      url: entry.name.slice(0, MAX_URL_CHARS),
      duration_ms: Math.round(entry.duration),
      size: entry.transferSize ?? 0,
    });
  };

  const observer = new PerformanceObserver((list) => {
    for (const entry of list.getEntries() as PerformanceResourceTiming[]) {
      emitEntry(entry);
    }
  });

  try {
    // buffered: true replays resources that loaded before this observer was installed
    // (e.g. the page's own initial JS/CSS/image loads).
    observer.observe({ type: 'resource', buffered: true });
  } catch {
    return () => {};
  }

  return () => observer.disconnect();
}

function installClickCapture(options: SessionEventCaptureOptions): () => void {
  const onClick = (event: MouseEvent): void => {
    const el = event.target instanceof Element ? event.target : null;
    if (!el) return;
    options.onEvent({
      ts: Date.now(),
      type: 'click',
      selector: buildSelector(el),
      text: clickText(el, options.maskAllText),
    });
  };

  document.addEventListener('click', onClick, { capture: true, passive: true });
  return () => {
    document.removeEventListener('click', onClick, { capture: true });
  };
}

function installNavigationCapture(options: SessionEventCaptureOptions): () => void {
  let lastUrl = currentRelativeUrl();

  const recordNavigation = (): void => {
    const url = currentRelativeUrl();
    if (url === lastUrl) return;
    options.onEvent({ ts: Date.now(), type: 'navigation', from: lastUrl, to: url });
    lastUrl = url;
  };

  // Kept unbound (and .apply'd) so uninstall restores the exact original —
  // .bind() would produce a new function and break identity on restore.
  const originalPushState = history.pushState;
  const originalReplaceState = history.replaceState;

  history.pushState = function patchedPushState(...args: Parameters<typeof history.pushState>) {
    originalPushState.apply(history, args);
    recordNavigation();
  };
  history.replaceState = function patchedReplaceState(
    ...args: Parameters<typeof history.replaceState>
  ) {
    originalReplaceState.apply(history, args);
    recordNavigation();
  };
  window.addEventListener('popstate', recordNavigation);

  return () => {
    history.pushState = originalPushState;
    history.replaceState = originalReplaceState;
    window.removeEventListener('popstate', recordNavigation);
  };
}

function currentRelativeUrl(): string {
  return (window.location.pathname + window.location.search + window.location.hash).slice(
    0,
    MAX_URL_CHARS,
  );
}

/** Short CSS-ish path: up to 3 ancestors, stopping early at the nearest id. */
function buildSelector(el: Element): string {
  const parts: string[] = [];
  let node: Element | null = el;
  while (node && node !== document.documentElement && parts.length < 3) {
    parts.unshift(describeElement(node));
    if (node.id) break;
    node = node.parentElement;
  }
  return parts.join(' > ').slice(0, MAX_SELECTOR_CHARS);
}

function describeElement(el: Element): string {
  const tag = el.tagName.toLowerCase();
  if (el.id) return `${tag}#${el.id}`;
  const classes = Array.from(el.classList).slice(0, 3);
  return classes.length > 0 ? `${tag}.${classes.join('.')}` : tag;
}

/** Input *values* are never read here — only rendered text content, and none at all under maskAllText (mirroring the replay's masking). */
function clickText(el: Element, maskAllText: boolean): string {
  if (maskAllText && !el.closest('.biwave-unmask')) return '';
  const raw = (el as HTMLElement).innerText ?? el.textContent ?? '';
  return raw.replace(/\s+/g, ' ').trim().slice(0, MAX_CLICK_TEXT_CHARS);
}

function stringifyArg(value: unknown): string {
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

function resolveUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return input.url;
}

function resolveMethod(input: RequestInfo | URL, init: RequestInit | undefined): string {
  if (init?.method) return init.method;
  if (input instanceof Request) return input.method;
  return 'GET';
}
